import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FunctionHelpService } from '../../services/functionHelp.service';
import { FetchOptionsState } from '../../../core/state/fetchOptions.state';
import { Store } from '@ngxs/store';
import { CollectionFilter } from '../../../core/jsonApi/BaseCollectionService';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FunctionalHelpJsonModel } from '../../functionalHelp.json.model';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { MatIconButton, MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompetencyFilterCBComponent } from '../../../utils/component/competency-filter-combobox/competency-cb.component';
import { ConsumerFilterCBComponent } from '../../../utils/component/consumer-filter-combobox/consumer-cb.component';
import { FreeFilterInputComponent } from '../../../utils/component/free-filter-input/free-filter-input.component';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TypeCBComponent } from '../../../utils/component/type-filter-combobox/type-cb.component';
import { ConfirmDialogComponent } from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsActions } from '../../../core/state/fetchOptions.actions';
import { EditFuntionalHelpComponent } from '../../edit-functionalHelp/edit-funtional-help.component';
import { AppSelectors } from '../../../app.state';
import { ConsumerJsonModel } from '../../../consumer/consumer.json.model';
import { SubjectJsonModel } from '../../../subject/subject.json.model';
import { MainPaginationComponent } from '../../../core/components/main-pagination/main-pagination.component';
import { CopyDialogComponent } from '../../../core/components/copy-dialog/copy-dialog.component';
import { TranslateComponent } from '../../../core/components/translation/translation.component';
import { getMinFilterLengthText } from '../../../core/util';
import { TranslocoModule } from '@jsverse/transloco';
import { FunctionalHelpEntityService } from '../../services/functional-help.entity.service';
import { LocalStorageService } from '../../../core/services/local-storage.service';

@Component({
  selector: 'pixl-funtional-help',
  imports: [
    MatIcon,
    MatSortModule,
    MatIconButton,
    MatTableModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatListModule,
    MatInputModule,
    DatePipe,
    ConsumerFilterCBComponent,
    CompetencyFilterCBComponent,
    FreeFilterInputComponent,
    MainPaginationComponent,
    TypeCBComponent,
    TranslateComponent,
    TranslocoModule
  ],
  templateUrl: './funtional-help.component.html',
  styleUrl: './funtional-help.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FuntionalHelpComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;

  private functionHelpService = inject(FunctionHelpService);
  private readonly store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private nameFilter: string | undefined;
  private consumerFilter: string | undefined;
  private competencyFilter: string | undefined;
  private exerciseKindFilter: string | undefined;
  private _liveAnnouncer = inject(LiveAnnouncer);
  private dialog = inject(MatDialog);
  private appState$ = this.store.select(AppSelectors.appState);
  private functionalHelpEntityService = inject(FunctionalHelpEntityService);
  private localStorageService = inject(LocalStorageService);

  public dataSource = new MatTableDataSource<FunctionalHelpJsonModel>([] as FunctionalHelpJsonModel[]);
  public displayedColumns: string[] = ['edit-actions', 'name', 'consumer', 'exerciseKind', 'competency', 'created'];
  public consumers: ConsumerJsonModel[] = []; // List of all available consumers
  public competencies: SubjectJsonModel[] = []; // List of all available competencies

  public filters: CollectionFilter[] = [];
  public readonly freeFilterLen = 3; // Min length of free filter input
  public pageSize = signal(25);
  public currentSelectedRow = signal<string | undefined>(undefined);
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  private setConsumerAndCompetency(functionalHelp: FunctionalHelpJsonModel): void {
    if (this.consumers) {
      const consumer = this.consumers.find((entry) => {
        return entry.id === functionalHelp.consumerId;
      });

      if (consumer) {
        functionalHelp.setConsumer(consumer);
      }
    }

    if (this.competencies) {
      const subject = this.competencies.find((entry) => {
        return entry.treeId === functionalHelp.subjectId;
      });

      if (subject) {
        functionalHelp.setCompetency(subject);
      }
    }
  }

  public ngOnInit(): void {
    this.dataSource.sort = this.sort;

    this.appState$.subscribe(({ isLoaded, consumers, subjects }) => {
      if (isLoaded) {
        this.consumers = consumers;
        this.competencies = subjects;
        this.getListData();
      }
    });
  }

  private setFilters(): void {
    this.currentSelectedRow.set(undefined);
    this.filters = [];

    if (this.consumerFilter) {
      this.filters.push({ fieldName: 'consumerId', value: this.consumerFilter });
    }

    if (this.competencyFilter) {
      this.filters.push({ fieldName: 'subjectId', value: this.competencyFilter });
    }

    if (this.nameFilter) {
      this.filters.push({ fieldName: 'name', value: this.nameFilter });
    }

    if (this.exerciseKindFilter) {
      this.filters.push({ fieldName: 'exerciseKind', value: this.exerciseKindFilter });
    }

    this.getListData();
  }

  private sortData(sort: Sort): void {
    this.store.dispatch(new FetchOptionsActions.UpdateSortFieldName(sort.active));
    this.store.dispatch(new FetchOptionsActions.UpdateSortDirection(sort.direction));
    this.getListData();
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public getCreatedDate(id: string): Date {
    return new Date(parseInt(id.substring(0, 8), 16) * 1000);
  }

  public setNameFilter(ev: string): void {
    this.nameFilter = (ev.length === 0 ? undefined : ev);
    this.setFilters();
  }

  public setConsumerFilter(ev: string): void {
    this.consumerFilter = ev;
    this.setFilters();
  }

  public setCompetencyFilter(ev: string): void {
    this.competencyFilter = ev;
    this.setFilters();
  }

  public setKindFilter(ev: string): void {
    this.exerciseKindFilter = ev;
    this.setFilters();
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public paginationChanged = (): void => {
    this.getListData();
  };

  private updateConsumerView(functionalHelp: FunctionalHelpJsonModel) {
    if (this.consumers) {
      const consumer = this.consumers.find((entry) => {
        return entry.id === functionalHelp.consumerId;
      });

      if (consumer) {
        functionalHelp.setConsumer(consumer);
      }
    }
  }

  private addFunctionalHelp2DB(functionalHelp: FunctionalHelpJsonModel, showEditDialog: boolean): void {
    this.functionHelpService
      .createAndSave(functionalHelp)
      .subscribe((functionalHelpFromServer) => {
        this.dataSource.data.push(functionalHelpFromServer);
        this.store.dispatch(new FetchOptionsActions.UpdateTotalItems(this.store.selectSnapshot(FetchOptionsState.getTotal) + 1)); // Update paginator
        this.updateConsumerView(functionalHelpFromServer);
        this.getListData();

        if (showEditDialog) {
          this.showFunctionalHelpDialog(functionalHelpFromServer);
        }
      });
  }

  public showFunctionalHelpDialog(editFunctionalHelp: FunctionalHelpJsonModel): void {
    this.currentSelectedRow.set(editFunctionalHelp.id);

    const dialogRef = this.dialog.open(EditFuntionalHelpComponent, {
      minWidth: '760px',
      width: '60vw',
      height: 'auto',
      minHeight: '570px',
      data: editFunctionalHelp
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((functionalHelp: FunctionalHelpJsonModel) => {
        if (functionalHelp) {
          if (!functionalHelp.hasId()) {
            // POST
            this.addFunctionalHelp2DB(functionalHelp, false);
          }
          else {
            // PATCH
            const [kind, subKind] = functionalHelp.exerciseKind.split('.');
            functionalHelp.exerciseKind = kind;
            functionalHelp.exerciseSubKind = subKind ? subKind : '';

            this.functionHelpService
              .update(functionalHelp)
              .subscribe((functionalHelpFromServer) => {
                this.dataSource.data = this.dataSource.data.map(item => item.id === functionalHelpFromServer.id ? functionalHelpFromServer : item);

                this.setConsumerAndCompetency(functionalHelpFromServer);

                this.cdr.markForCheck();
              });
          }
        }
      });
  }

  public addFunctionalHelp(): void {
    this.showFunctionalHelpDialog(new FunctionalHelpJsonModel({ name: '' }));
  }

  public editFunctionalHelp(functionalHelp: FunctionalHelpJsonModel): void {
    this.showFunctionalHelpDialog(functionalHelp);
  }

  public confirmDelete(functionalHelp: FunctionalHelpJsonModel) {
    this.currentSelectedRow.set(functionalHelp.id);

    const dialogRef = this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          id: functionalHelp.id,
          name: functionalHelp.name,
          itemType: 'Funktionale Hilfe/Tipps',
          deleteFn: () => {
            return functionalHelp.destroy();
          }
        },
      });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.dataSource.data = this.dataSource.data.filter(value => value.id !== functionalHelp.id);
          this.store.dispatch(new FetchOptionsActions.UpdateTotalItems(this.store.selectSnapshot(FetchOptionsState.getTotal) - 1)); // Update paginator
          this.getListData();
        }
      });
  }

  public copyFunctionalHelp(functionalHelp: FunctionalHelpJsonModel) {
    this.currentSelectedRow.set(functionalHelp.id);

    const dialogRef = this.dialog.open(CopyDialogComponent, {
      minWidth: '400px',
      data: {
        name: functionalHelp.name + ' Kopie',
        consumerId: functionalHelp.consumerId,
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer) => {
        if (answer) {
          const copyFuntionalHelp = new FunctionalHelpJsonModel();

          copyFuntionalHelp.consumerId = answer.consumerId;
          copyFuntionalHelp.exerciseKind = functionalHelp.exerciseKind;
          copyFuntionalHelp.exerciseSubKind = functionalHelp.exerciseSubKind;
          copyFuntionalHelp.name = answer.name ? answer.name : (functionalHelp.name + ' Kopie');
          copyFuntionalHelp.subjectId = functionalHelp.subjectId;
          copyFuntionalHelp.markdownText = functionalHelp.markdownText;
          copyFuntionalHelp.markupText = functionalHelp.markupText;
          copyFuntionalHelp.desktopAudio = functionalHelp.desktopAudio;
          copyFuntionalHelp.mobileAudio = functionalHelp.mobileAudio;
          copyFuntionalHelp.image = functionalHelp.image;
          copyFuntionalHelp.approval = functionalHelp.approval;
          copyFuntionalHelp.active = functionalHelp.active;
          copyFuntionalHelp.setConsumer(functionalHelp.getConsumer());
          copyFuntionalHelp.setCompetency(functionalHelp.getCompetency());

          this.addFunctionalHelp2DB(copyFuntionalHelp, true);
        }
      });
  }

  public getListData(): void {
    this.functionHelpService.getAllWithPagination({}, {
      pageNumber: this.store.selectSnapshot(FetchOptionsState.getPageNumber),
      pageSize: this.store.selectSnapshot(FetchOptionsState.getPageSize),
      filters: this.filters,
      sortFieldName: this.store.selectSnapshot(FetchOptionsState.getSortFieldName),
      sortDirection: this.store.selectSnapshot(FetchOptionsState.getSortDirection)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const [data] = response;
          const functionalHelps = data.map(r => {
            r.setConsumer(this.consumers.find((model) => model.id == r.consumerId) as ConsumerJsonModel);
            r.setCompetency(this.competencies.find((model) => model.treeId == r.subjectId) as SubjectJsonModel);
            return r;
          });
          this.dataSource = new MatTableDataSource<FunctionalHelpJsonModel>(functionalHelps);
          this.cdr.markForCheck();
        },
        error: () => {
          // is handled in requestInterceptor.ts
        },
      });
  }
}
